import React, { useEffect, useState } from 'react';
import axios from 'axios';
import DashboardHeader from '../DashboardHeader/DashboardHeader';
import { transactionsRoute } from '../../../utils/APIRoutes';
import './TransactionHistoryComponent.scss';

type Transaction = {
  orderId: string;
  package: any;
  amount: number;
  status: 'FAILED' | 'PENDING' | 'COMPLETED';
  date: string;
  paymentMethod: string;
  paymentResult: any;
  create_time: Date;
};

const TransactionHistoryComponent: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const response = await axios.get(transactionsRoute, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });
        setTransactions(response.data);
      } catch (err) {
        console.error('Failed to load transactions', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, []);

  const getStatusBadge = (status?: string) => {
    const normalized = status?.toLowerCase();

    switch (normalized) {
      case 'completed':
      case 'success':
      case 'paid':
        return (
          <span className="badge bg-success-subtle text-success px-2.5 py-1.5 rounded-pill fw-semibold">
            Completed
          </span>
        );
      case 'pending':
        return (
          <span className="badge bg-warning-subtle text-warning-emphasis px-2.5 py-1.5 rounded-pill fw-semibold">
            Pending
          </span>
        );
      case 'failed':
      case 'cancelled':
        return (
          <span className="badge bg-danger-subtle text-danger px-2.5 py-1.5 rounded-pill fw-semibold">
            Failed
          </span>
        );
      default:
        return (
          <span className="badge bg-secondary-subtle text-secondary px-2.5 py-1.5 rounded-pill fw-semibold">
            {status || 'Unknown'}
          </span>
        );
    }
  };

  return (
    <div className="main-content">
      <DashboardHeader />
      <div className="container-fluid card shadow-sm p-4 mt-4">
        <h4 className="mb-3">Billing History</h4>

        {loading ? (
          <div>Loading...</div>
        ) : transactions.length === 0 ? (
          <p>No transactions found.</p>
        ) : (
          /* Wrap your existing transactions table in .listing-table */
          <div className="property-listing listing-table mt-0">
            <div className="table-responsive">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th scope="col">Order Id</th>
                    <th scope="col">Package</th>
                    <th scope="col">Amount</th>
                    <th scope="col">Status</th>
                    <th scope="col">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions?.length > 0 ? (
                    transactions.map((txn, index) => (
                      <tr key={txn?.orderId || index}>
                        <td
                          data-label="Order Id"
                          className="fw-semibold text-secondary"
                        >
                          #{txn?.orderId || '-'}
                        </td>
                        <td
                          data-label="Package"
                          className="fw-medium text-dark"
                        >
                          {txn?.package?.name || '-'}
                        </td>
                        <td data-label="Amount">
                          <span className="price-tag fw-bold">
                            {txn?.paymentResult?.amount?.value
                              ? `₹${Number(txn.paymentResult.amount.value).toLocaleString('en-IN')}`
                              : '-'}
                          </span>
                        </td>
                        <td data-label="Status">
                          {getStatusBadge(txn?.status)}
                        </td>
                        <td data-label="Date" className="text-muted">
                          {txn?.create_time
                            ? new Date(txn.create_time).toLocaleString(
                                'en-IN',
                                {
                                  dateStyle: 'medium',
                                  timeStyle: 'short',
                                }
                              )
                            : '-'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center py-4 text-muted">
                        No transactions found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionHistoryComponent;
